import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FormSection } from "../../../components/FormSection";
import { ColorInput } from "../../../components/ColorInput";
import { useGetPaymentMethodsQuery } from "../../../query/paymentMethodsQuery";
import { PaymentMethodType } from "@KassaroAB/shared/types/enums/PaymentMethodType";
import {
  PAYMENT_LINK_DEFAULT_VALIDITY_DAYS,
  PAYMENT_LINK_MAX_VALIDITY_DAYS,
  type PaymentLinkPaymentMethod,
} from "@KassaroAB/shared/types/paymentLink";
import type {
  PaymentMethodPaymentLink,
  RestPaymentMethod,
} from "@KassaroAB/shared/types/paymentMethods/paymentMethod";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { usePaymentMethodForm } from "../paymentMethodFormHook";
import {
  PaymentMethodFormContainer,
  type PaymentMethodFormProps,
} from "./PaymentMethodFormContainer";
import { FieldRow, Fields, TextField, ToggleField } from "./paymentMethodFields";

const Hint = styled.span`
  color: ${colors.textMuted};
  font-size: ${fontSizes.small}px;
`;

const LINKABLE_TYPES = [
  PaymentMethodType.SWISH_COMMERCE,
  PaymentMethodType.VIVA,
  PaymentMethodType.GIFT_CARD,
  PaymentMethodType.CREDIT_NOTE,
];

const toLinkPaymentMethod = (
  paymentMethod: RestPaymentMethod,
): PaymentLinkPaymentMethod | null => {
  if (paymentMethod.type === PaymentMethodType.VIVA) {
    return {
      id: paymentMethod.id,
      name: paymentMethod.name,
      enabled: true,
      type: PaymentMethodType.VIVA_SMART_CHECKOUT,
      sourceCode: "NOT_SET",
    };
  }
  if (
    paymentMethod.type === PaymentMethodType.SWISH_COMMERCE ||
    paymentMethod.type === PaymentMethodType.GIFT_CARD ||
    paymentMethod.type === PaymentMethodType.CREDIT_NOTE
  ) {
    return {
      id: paymentMethod.id,
      name: paymentMethod.name,
      enabled: true,
      type: paymentMethod.type,
    };
  }
  return null;
};

const parseValidityDays = (value: string) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed < 1) {
    return PAYMENT_LINK_DEFAULT_VALIDITY_DAYS;
  }
  return Math.min(parsed, PAYMENT_LINK_MAX_VALIDITY_DAYS);
};

export const PaymentLinkForm = ({
  formId,
  store,
  paymentMethod,
  savedTick,
  onSubmit,
  onDirtyChange,
}: PaymentMethodFormProps<Omit<PaymentMethodPaymentLink, "id">>) => {
  const { t } = useTranslation();
  const { data: storePaymentMethods } = useGetPaymentMethodsQuery(store.id);
  const linkablePaymentMethods = (storePaymentMethods ?? []).filter(
    (storePaymentMethod) => LINKABLE_TYPES.includes(storePaymentMethod.type),
  );
  const form = usePaymentMethodForm({
    defaultValues: {
      name: paymentMethod.name,
      color: paymentMethod.color ?? null,
      linkPaymentMethods: paymentMethod.linkPaymentMethods,
      validityDays: String(paymentMethod.validityDays),
      fiscalize: paymentMethod.fiscalize ?? false,
    },
    onSubmit: ({ value }) =>
      onSubmit({
        type: PaymentMethodType.PAYMENT_LINK,
        name: value.name,
        color: value.color,
        bookkeepingAccountId: paymentMethod.bookkeepingAccountId ?? null,
        linkPaymentMethods: value.linkPaymentMethods,
        validityDays: parseValidityDays(value.validityDays),
        fiscalize: value.fiscalize,
      }),
  });

  return (
    <PaymentMethodFormContainer
      formId={formId}
      form={form}
      savedTick={savedTick}
      onDirtyChange={onDirtyChange}
    >
      <FormSection title={t("paymentMethodForm.basicInformation")}>
        <Fields>
          <form.Field name="name">
            {(field) => (
              <TextField
                label={t("paymentMethodForm.name")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <TextField
            label={t("paymentMethodForm.type")}
            value={t("paymentMethodType.PAYMENT_LINK")}
            onChange={() => {}}
            disabled
          />
          <form.Field name="color">
            {(field) => (
              <FieldRow label={t("paymentMethodForm.color")}>
                <ColorInput
                  value={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                  clearLabel={t("paymentMethodForm.clearColor")}
                />
              </FieldRow>
            )}
          </form.Field>
        </Fields>
      </FormSection>
      <FormSection title={t("paymentMethodForm.paymentLink.paymentMethods")}>
        <Fields>
          <Hint>
            {linkablePaymentMethods.length === 0
              ? t("paymentMethodForm.paymentLink.noEligible")
              : t("paymentMethodForm.paymentLink.paymentMethodsHint")}
          </Hint>
          <form.Field name="linkPaymentMethods">
            {(field) =>
              linkablePaymentMethods.map((storePaymentMethod) => {
                const linked = field.state.value.some(
                  (entry) => entry.id === storePaymentMethod.id,
                );
                return (
                  <ToggleField
                    key={storePaymentMethod.id}
                    label={storePaymentMethod.name}
                    value={linked}
                    onChange={(enabled) => {
                      const entry = toLinkPaymentMethod(storePaymentMethod);
                      if (!entry) {
                        return;
                      }
                      field.handleChange(
                        enabled
                          ? [...field.state.value, entry]
                          : field.state.value.filter(
                              (current) => current.id !== storePaymentMethod.id,
                            ),
                      );
                    }}
                  />
                );
              })
            }
          </form.Field>
        </Fields>
      </FormSection>
      <FormSection title={t("paymentMethodForm.paymentLink.cashRegister")}>
        <Fields>
          <form.Field name="fiscalize">
            {(field) => (
              <ToggleField
                label={t("paymentMethodForm.paymentLink.fiscalize")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <Hint>{t("paymentMethodForm.paymentLink.fiscalizeHint")}</Hint>
          <form.Field name="validityDays">
            {(field) => (
              <TextField
                label={t("paymentMethodForm.paymentLink.validityDays")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                inputMode="numeric"
              />
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </PaymentMethodFormContainer>
  );
};
